import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import {
  Zap, RefreshCw, Brain, MessageSquare, FileText, Lightbulb,
  ArrowRight, CheckCircle2, Clock, AlertTriangle, ChevronDown,
  ChevronRight, Play, Pause, Sparkles, TrendingUp, BookOpen,
  BarChart3,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────

interface ProactiveLogEntry {
  id: string;
  org_id: string;
  triggers_json: TriggerCondition[];
  actions_json: ActionTaken[];
  total_score: number;
  created_at: string;
}

interface TriggerCondition {
  type: string;
  severity: string;
  detail: string;
  score: number;
}

interface ActionTaken {
  action: string;
  success: boolean;
  detail: string;
  generated_id?: string;
}

interface OrgRow {
  id: string;
  name: string;
  status: string;
}

interface PipelineStatus {
  org_id: string;
  org_name: string;
  org_status: string;
  lastThinking: string | null;
  pendingThinking: number;
  lastComms: string | null;
  lastSummary: string | null;
  lastIdeas: string | null;
  pendingRecs: number;
  recentLog: ProactiveLogEntry | null;
  healthScore: number; // 0-100, how "up to date" the org is
}

// ── Helpers ───────────────────────────────────────────────────────

function triggerIcon(type: string) {
  switch (type) {
    case 'knowledge_updated': return BookOpen;
    case 'thinking_gap': return Brain;
    case 'comms_gap': return MessageSquare;
    case 'exec_summary_gap': return FileText;
    case 'ideas_gap': return Lightbulb;
    case 'health_flag_high': return AlertTriangle;
    case 'council_feedback': return TrendingUp;
    case 'low_activation': return TrendingUp;
    default: return Zap;
  }
}

function severityColor(severity: string) {
  if (severity === 'critical') return 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30';
  if (severity === 'high') return 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/30';
  if (severity === 'medium') return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/30';
  return 'bg-muted text-muted-foreground border-muted';
}

function computeHealthScore(ps: Omit<PipelineStatus, 'healthScore'>): number {
  let score = 100;
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;

  // Thinking freshness (max -30)
  if (!ps.lastThinking) { score -= 30; }
  else {
    const days = (now - new Date(ps.lastThinking).getTime()) / dayMs;
    if (days > 14) score -= 30;
    else if (days > 7) score -= 15;
    else if (days > 3) score -= 5;
  }

  // Comms freshness (max -25)
  if (!ps.lastComms) { score -= 25; }
  else {
    const days = (now - new Date(ps.lastComms).getTime()) / dayMs;
    if (days > 14) score -= 25;
    else if (days > 7) score -= 12;
  }

  // Summary freshness (max -15)
  if (!ps.lastSummary) { score -= 15; }
  else {
    const days = (now - new Date(ps.lastSummary).getTime()) / dayMs;
    if (days > 7) score -= 15;
  }

  // Ideas freshness (max -10)
  if (!ps.lastIdeas) { score -= 10; }
  else {
    const days = (now - new Date(ps.lastIdeas).getTime()) / dayMs;
    if (days > 2) score -= 10;
  }

  // Pending recs (max -10)
  if (ps.pendingRecs > 5) score -= 10;
  else if (ps.pendingRecs > 2) score -= 5;

  // Pending thinking (max -10)
  if (ps.pendingThinking > 3) score -= 10;
  else if (ps.pendingThinking > 1) score -= 5;

  return Math.max(0, Math.min(100, score));
}

function healthColor(score: number) {
  if (score >= 80) return { bg: 'bg-green-500', text: 'text-green-700 dark:text-green-400', label: 'Healthy' };
  if (score >= 60) return { bg: 'bg-yellow-500', text: 'text-yellow-700 dark:text-yellow-400', label: 'Needs Attention' };
  if (score >= 40) return { bg: 'bg-orange-500', text: 'text-orange-700 dark:text-orange-400', label: 'Behind' };
  return { bg: 'bg-red-500', text: 'text-red-700 dark:text-red-400', label: 'Critical' };
}

function actionLabel(action: string) {
  switch (action) {
    case 'generate_thinking': return 'Thinking Session';
    case 'generate_comms': return 'Comms Package';
    case 'generate_exec_summary': return 'Exec Summary';
    case 'create_recommendation': return 'Recommendation';
    case 'create_output': return 'Output Drafts';
    default: return action.replace(/_/g, ' ');
  }
}

// ── Main component ────────────────────────────────────────────────

export default function ProactivePipeline() {
  const navigate = useNavigate();

  const [pipelines, setPipelines] = useState<PipelineStatus[]>([]);
  const [recentLogs, setRecentLogs] = useState<ProactiveLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runningSingle, setRunningSingle] = useState<string | null>(null);
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [orgsRes, thinkingRes, commsRes, summaryRes, ideasRes, recsRes, logRes] = await Promise.all([
        supabase.from('organizations').select('id, name, status').order('name'),
        supabase.from('cosmo_thinking_sessions').select('org_id, status, created_at').order('created_at', { ascending: false }),
        supabase.from('council_comms_packages').select('org_id, generated_at, status').order('generated_at', { ascending: false }),
        supabase.from('org_executive_summaries').select('org_id, created_at').order('created_at', { ascending: false }),
        supabase.from('org_prompt_ideas').select('org_id, generated_at').order('generated_at', { ascending: false }),
        supabase.from('cosmo_recommendations').select('org_id, council_response').is('council_response', null),
        // Try to load proactive log (table may not exist yet)
        supabase.from('cosmo_proactive_log' as any).select('*').order('created_at', { ascending: false }).limit(50),
      ]);

      const orgs: OrgRow[] = orgsRes.data || [];
      const thinking = thinkingRes.data || [];
      const comms = commsRes.data || [];
      const summaries = summaryRes.data || [];
      const ideas = ideasRes.data || [];
      const recs = recsRes.data || [];
      const logs: ProactiveLogEntry[] = (logRes.data as any) || [];

      setRecentLogs(logs);

      // Build pipeline status per org
      const statuses: PipelineStatus[] = orgs.map(org => {
        const orgThinking = thinking.filter((t: any) => t.org_id === org.id);
        const orgComms = comms.filter((c: any) => c.org_id === org.id);
        const orgSummaries = summaries.filter((s: any) => s.org_id === org.id);
        const orgIdeas = ideas.filter((i: any) => i.org_id === org.id);
        const orgRecs = recs.filter((r: any) => r.org_id === org.id);
        const orgLogs = logs.filter((l: any) => l.org_id === org.id);

        const base = {
          org_id: org.id,
          org_name: org.name,
          org_status: org.status,
          lastThinking: orgThinking[0]?.created_at || null,
          pendingThinking: orgThinking.filter((t: any) => t.status === 'draft').length,
          lastComms: orgComms[0]?.generated_at || null,
          lastSummary: (orgSummaries[0] as any)?.generated_at || null,
          lastIdeas: orgIdeas[0]?.generated_at || null,
          pendingRecs: orgRecs.length,
          recentLog: orgLogs[0] || null,
        };

        return { ...base, healthScore: computeHealthScore(base) };
      });

      // Sort: worst health first
      statuses.sort((a, b) => a.healthScore - b.healthScore);
      setPipelines(statuses);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('ProactivePipeline load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Run proactive engine for all orgs ──
  const runAll = async () => {
    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke('cosmo-proactive-engine', {
        body: { all_orgs: true },
      });

      if (error) {
        toast.error(`Engine error: ${error.message}`);
      } else {
        const summary = data as any;
        toast.success(
          `Proactive scan complete: ${summary.orgs_with_actions} orgs updated, ${summary.total_actions} actions taken`
        );
      }

      await load(); // refresh
    } catch (err) {
      toast.error(`Failed: ${String(err)}`);
    } finally {
      setRunning(false);
    }
  };

  // ── Run for single org ──
  const runSingle = async (orgId: string) => {
    setRunningSingle(orgId);
    try {
      const { data, error } = await supabase.functions.invoke('cosmo-proactive-engine', {
        body: { org_id: orgId },
      });

      if (error) {
        toast.error(`Error: ${error.message}`);
      } else {
        const result = data as any;
        if (result.actions_taken?.length > 0) {
          toast.success(`${result.org_name}: ${result.actions_taken.length} actions taken`);
        } else {
          toast(`${result.org_name}: ${result.skipped_reason || 'No actions needed'}`);
        }
      }

      await load();
    } catch (err) {
      toast.error(`Failed: ${String(err)}`);
    } finally {
      setRunningSingle(null);
    }
  };

  // ── Summary stats ──
  const avgHealth = pipelines.length > 0
    ? Math.round(pipelines.reduce((s, p) => s + p.healthScore, 0) / pipelines.length)
    : 0;
  const criticalCount = pipelines.filter(p => p.healthScore < 40).length;
  const totalPendingThinking = pipelines.reduce((s, p) => s + p.pendingThinking, 0);
  const totalPendingRecs = pipelines.reduce((s, p) => s + p.pendingRecs, 0);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-64">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Sparkles className="h-5 w-5 animate-pulse" />
          <span className="text-sm">Loading proactive pipeline…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Proactive Pipeline
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Cosmo's automated generation engine — what's fresh, what needs attention, what's queued
            {lastRefreshed && ` · refreshed ${formatDistanceToNow(lastRefreshed, { addSuffix: true })}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={runAll} disabled={running}>
            {running ? (
              <>
                <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Running…
              </>
            ) : (
              <>
                <Play className="h-3.5 w-3.5 mr-1.5" />
                Run All Orgs
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Avg Pipeline Health</p>
            <p className={`text-2xl font-bold mt-1 ${healthColor(avgHealth).text}`}>{avgHealth}%</p>
          </CardContent>
        </Card>
        <Card className={criticalCount > 0 ? 'border-red-500/20' : ''}>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Critical Orgs</p>
            <p className={`text-2xl font-bold mt-1 ${criticalCount > 0 ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'}`}>
              {criticalCount}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Pending Thinking</p>
            <p className="text-2xl font-bold mt-1">{totalPendingThinking}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Pending Recs</p>
            <p className="text-2xl font-bold mt-1">{totalPendingRecs}</p>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline per org */}
      <div className="space-y-2">
        {pipelines.map(ps => {
          const hc = healthColor(ps.healthScore);
          const isExpanded = expandedOrg === ps.org_id;
          const isRunning = runningSingle === ps.org_id;

          return (
            <Card key={ps.org_id} className="relative overflow-hidden">
              {/* Health stripe */}
              <div className={`absolute left-0 top-0 bottom-0 w-1 ${hc.bg}`} />

              <CardContent className="pl-5 pt-3 pb-3">
                {/* Header row */}
                <div className="flex items-center justify-between gap-3">
                  <div
                    className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                    onClick={() => setExpandedOrg(isExpanded ? null : ps.org_id)}
                  >
                    {isExpanded
                      ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                      : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    }
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm truncate">{ps.org_name}</p>
                        <Badge variant="outline" className={`text-xs shrink-0 ${severityColor(
                          ps.healthScore >= 80 ? 'low' : ps.healthScore >= 60 ? 'medium' : ps.healthScore >= 40 ? 'high' : 'critical'
                        )}`}>
                          {hc.label} · {ps.healthScore}%
                        </Badge>
                      </div>

                      {/* Quick status chips */}
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Brain className="h-3 w-3" />
                          {ps.lastThinking
                            ? formatDistanceToNow(new Date(ps.lastThinking), { addSuffix: true })
                            : 'never'
                          }
                          {ps.pendingThinking > 0 && <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 ml-0.5">{ps.pendingThinking} pending</Badge>}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" />
                          {ps.lastComms
                            ? formatDistanceToNow(new Date(ps.lastComms), { addSuffix: true })
                            : 'never'
                          }
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {ps.lastSummary
                            ? formatDistanceToNow(new Date(ps.lastSummary), { addSuffix: true })
                            : 'never'
                          }
                        </span>
                        {ps.pendingRecs > 0 && (
                          <span className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400">
                            <Lightbulb className="h-3 w-3" />
                            {ps.pendingRecs} recs waiting
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs"
                      onClick={() => runSingle(ps.org_id)}
                      disabled={isRunning || running}
                    >
                      {isRunning ? (
                        <RefreshCw className="h-3 w-3 animate-spin" />
                      ) : (
                        <><Zap className="h-3 w-3 mr-1" /> Run</>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={() => navigate(`/admin/${ps.org_id}`)}
                    >
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Expanded: recent proactive log */}
                {isExpanded && ps.recentLog && (
                  <div className="mt-3 ml-7 border-t border-border/50 pt-3 space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Last proactive scan · {formatDistanceToNow(new Date(ps.recentLog.created_at), { addSuffix: true })}
                    </p>

                    {/* Triggers */}
                    {ps.recentLog.triggers_json?.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Triggers detected:</p>
                        {ps.recentLog.triggers_json.map((t, i) => {
                          const TIcon = triggerIcon(t.type);
                          return (
                            <div key={i} className="flex items-center gap-2 text-xs">
                              <TIcon className="h-3 w-3 text-muted-foreground" />
                              <Badge variant="outline" className={`text-[10px] ${severityColor(t.severity)}`}>
                                {t.severity}
                              </Badge>
                              <span>{t.detail}</span>
                              <span className="text-muted-foreground/60 ml-auto">{t.score} pts</span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Actions */}
                    {ps.recentLog.actions_json?.length > 0 && (
                      <div className="space-y-1 mt-2">
                        <p className="text-xs font-medium text-muted-foreground">Actions taken:</p>
                        {ps.recentLog.actions_json.map((a, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            {a.success
                              ? <CheckCircle2 className="h-3 w-3 text-green-500" />
                              : <AlertTriangle className="h-3 w-3 text-red-500" />
                            }
                            <span className="font-medium">{actionLabel(a.action)}</span>
                            <span className="text-muted-foreground truncate">{a.detail}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {isExpanded && !ps.recentLog && (
                  <div className="mt-3 ml-7 border-t border-border/50 pt-3">
                    <p className="text-xs text-muted-foreground">
                      No proactive scan history yet. Click "Run" to trigger the first scan.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent activity log */}
      {recentLogs.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Recent Engine Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-0">
            {recentLogs.slice(0, 15).map(log => {
              const orgName = pipelines.find(p => p.org_id === log.org_id)?.org_name || 'Unknown';
              const actionCount = log.actions_json?.length || 0;
              const successCount = log.actions_json?.filter(a => a.success).length || 0;

              return (
                <div key={log.id} className="flex items-center justify-between text-sm py-2 border-b border-border/40 last:border-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-medium shrink-0">{orgName}</span>
                    <span className="text-muted-foreground text-xs truncate">
                      {actionCount > 0
                        ? `${successCount}/${actionCount} actions succeeded`
                        : 'No actions needed'
                      }
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                    </span>
                    <Badge variant="outline" className="text-xs">{log.total_score} pts</Badge>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* How it works */}
      <div className="border rounded-lg p-4 space-y-2 bg-muted/20">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">How the Proactive Engine Works</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-muted-foreground">
          <div className="flex items-center gap-2"><Brain className="h-3 w-3 text-primary" /> Detects thinking gaps, health flags, low activation</div>
          <div className="flex items-center gap-2"><Sparkles className="h-3 w-3 text-primary" /> Auto-generates thinking sessions from org context</div>
          <div className="flex items-center gap-2"><MessageSquare className="h-3 w-3 text-primary" /> Creates comms packages when gap exceeds 7 days</div>
          <div className="flex items-center gap-2"><FileText className="h-3 w-3 text-primary" /> Refreshes exec summaries weekly</div>
          <div className="flex items-center gap-2"><Lightbulb className="h-3 w-3 text-primary" /> Cascades recommendations from approved thinking</div>
          <div className="flex items-center gap-2"><TrendingUp className="h-3 w-3 text-primary" /> Learns from council feedback to improve over time</div>
        </div>
      </div>
    </div>
  );
}
